import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../lib/firebase";

const ADMIN_EMAIL = "stagepassadmin@gmail.com";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const isAdminEmail = firebaseUser.email === ADMIN_EMAIL;

        let userRole = isAdminEmail ? "admin" : "user";

        if (!isAdminEmail) {
          // Try custom claims first (set by Cloud Functions when available)
          try {
            const tokenResult = await firebaseUser.getIdTokenResult(true);
            if (tokenResult.claims.role) {
              userRole = tokenResult.claims.role;
            }
          } catch (e) {
            // ignore, fall through to Firestore
          }
        }

        try {
          const profileDoc = await getDoc(doc(db, "users", firebaseUser.uid));
          if (profileDoc.exists()) {
            const data = profileDoc.data();
            if (isAdminEmail && data.role !== "admin") {
              await setDoc(doc(db, "users", firebaseUser.uid), { role: "admin" }, { merge: true });
            }
            // Use Firestore role as authoritative source when custom claims don't have one
            const firestoreRole = isAdminEmail ? "admin" : (data.role || userRole);
            if (!isAdminEmail && userRole === "user" && data.role && data.role !== "user") {
              userRole = data.role;
            }
            setProfile({ ...data, role: firestoreRole });
          } else {
            const newProfile = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              displayName: firebaseUser.displayName || "",
              photoURL: firebaseUser.photoURL || "",
              role: userRole,
              createdAt: serverTimestamp(),
              status: "active",
            };
            await setDoc(doc(db, "users", firebaseUser.uid), newProfile);
            setProfile(newProfile);
          }
        } catch (e) {
          console.error("Profile load error:", e);
        }

        setRole(userRole);
      } else {
        setUser(null);
        setProfile(null);
        setRole(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = (email, password) => signInWithEmailAndPassword(auth, email, password);

  const register = async (email, password, displayName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName });
    await setDoc(doc(db, "users", cred.user.uid), {
      uid: cred.user.uid,
      email,
      displayName,
      photoURL: "",
      role: "user",
      createdAt: serverTimestamp(),
      status: "active",
    });
    return cred;
  };

  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const cred = await signInWithPopup(auth, provider);
    const profileDoc = await getDoc(doc(db, "users", cred.user.uid));
    const isAdminEmail = cred.user.email === ADMIN_EMAIL;
    if (!profileDoc.exists()) {
      await setDoc(doc(db, "users", cred.user.uid), {
        uid: cred.user.uid,
        email: cred.user.email,
        displayName: cred.user.displayName || "",
        photoURL: cred.user.photoURL || "",
        role: isAdminEmail ? "admin" : "user",
        createdAt: serverTimestamp(),
        status: "active",
      });
    } else if (isAdminEmail) {
      await setDoc(doc(db, "users", cred.user.uid), { role: "admin" }, { merge: true });
    }
    return cred;
  };

  const logout = () => signOut(auth);

  const isAdmin = role === "admin";
  const isCreator = role === "creator" || role === "admin";

  return (
    <AuthContext.Provider value={{ user, profile, role, loading, login, register, loginWithGoogle, logout, isAdmin, isCreator }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
